/**
 * P-Ticket Handler - Tradovate Rate Limiting System
 *
 * When Tradovate detects abuse (too many requests), it returns:
 * - `p-ticket`: Token required for retry
 * - `p-time`: Penalty seconds to wait before retry
 * - `p-captcha` (optional): CAPTCHA challenge required
 *
 * This handler automatically:
 * 1. Detects p-ticket in server response
 * 2. Waits penalty period (or shows CAPTCHA modal)
 * 3. Retries request with p-ticket (and captcha token if applicable)
 *
 * Ported from: p_ticket_handler.rs (354 lines)
 */

/**
 * Penalty information from server
 */
export interface PenaltyInfo {
  /** P-ticket token (must include in retry) */
  pTicket: string;

  /** Penalty time in seconds */
  pTime: number;

  /** Whether CAPTCHA is required */
  requiresCaptcha: boolean;
}

/**
 * Global CAPTCHA handler
 *
 * Should be registered by the app to open CAPTCHA modal.
 * Returns captcha response token after user solves.
 */
export type CaptchaHandler = (pTicket: string, pTime: number) => Promise<string>;

/**
 * Global captcha handler storage
 */
let globalCaptchaHandler: CaptchaHandler | null = null;

/**
 * Register global CAPTCHA handler
 *
 * @param handler - Function that shows CAPTCHA modal and returns token
 *
 * @example
 * ```typescript
 * // In auth router/app initialization
 * registerGlobalCaptchaHandler(async (pTicket, pTime) => {
 *   return new Promise((resolve, reject) => {
 *     openCaptchaModal({
 *       pTicket,
 *       pTime,
 *       onSuccess: (token) => resolve(token),
 *       onCancel: () => reject(new Error('User cancelled'))
 *     });
 *   });
 * });
 * ```
 */
export function registerGlobalCaptchaHandler(handler: CaptchaHandler): void {
  globalCaptchaHandler = handler;
  console.log('✅ Global CAPTCHA handler registered');
}

/**
 * P-Ticket Handler - Static utility class
 */
export class PTicketHandler {
  /**
   * Extract penalty information from server response
   *
   * @param response - Server response (any object)
   * @returns Penalty info if p-ticket present, null otherwise
   */
  static extractPenaltyInfo(response: any): PenaltyInfo | null {
    if (response && typeof response['p-ticket'] === 'string') {
      return {
        pTicket: response['p-ticket'],
        pTime: typeof response['p-time'] === 'number' ? response['p-time'] : 0,
        requiresCaptcha: 'p-captcha' in response
      };
    }
    return null;
  }

  /**
   * Add p-ticket to request body
   *
   * @param request - Original request body
   * @param pTicket - P-ticket token from penalty
   * @returns Request with p-ticket added
   */
  static addPTicketToRequest(request: any, pTicket: string): any {
    if (!request || typeof request !== 'object' || Array.isArray(request)) {
      return { 'p-ticket': pTicket };
    }

    return {
      ...request,
      'p-ticket': pTicket
    };
  }

  /**
   * Add p-captcha response to request body
   *
   * @param request - Request body (already has p-ticket)
   * @param captchaToken - CAPTCHA response token
   * @returns Request with p-captcha added
   */
  static addCaptchaToRequest(request: any, captchaToken: string): any {
    if (!request || typeof request !== 'object' || Array.isArray(request)) {
      return { 'p-captcha': captchaToken };
    }

    return {
      ...request,
      'p-captcha': captchaToken
    };
  }

  /**
   * Wait for penalty period
   *
   * @param penaltySeconds - How long to wait
   */
  static async waitForPenalty(penaltySeconds: number): Promise<void> {
    if (penaltySeconds === 0) return;

    console.log(`⏳ Waiting ${penaltySeconds}s for p-ticket penalty...`);
    await new Promise(resolve => setTimeout(resolve, penaltySeconds * 1000));
    console.log('✅ Penalty wait complete');
  }

  /**
   * Handle p-ticket retry automatically
   *
   * This is called by HttpTransport after receiving a response.
   * If response contains p-ticket, automatically handles retry.
   *
   * @param response - Server response
   * @param originalRequest - Original request body
   * @param retryFn - Function to retry the request
   * @returns Retry result if p-ticket handled, null if no p-ticket
   *
   * @example
   * ```typescript
   * // In HttpTransport.post():
   * const response = await fetch(...);
   * const data = await response.json();
   *
   * // Check for p-ticket and retry if present
   * const retryResult = await PTicketHandler.handlePTicketRetry(
   *   data,
   *   body,
   *   (retryBody) => this.post(path, retryBody)  // Recursive retry
   * );
   *
   * if (retryResult !== null) {
   *   return retryResult;  // Retry succeeded
   * }
   *
   * return data;  // Original response (no p-ticket)
   * ```
   */
  static async handlePTicketRetry<T>(
    response: any,
    originalRequest: any,
    retryFn: (retryRequest: any) => Promise<T>
  ): Promise<T | null> {
    const penaltyInfo = this.extractPenaltyInfo(response);
    if (!penaltyInfo) {
      return null;  // No p-ticket, no retry needed
    }

    console.log(
      `⚠️ P-ticket detected: ${penaltyInfo.pTicket}, ` +
      `penalty: ${penaltyInfo.pTime}s, ` +
      `captcha: ${penaltyInfo.requiresCaptcha}`
    );

    // Add p-ticket to request
    let retryRequest = this.addPTicketToRequest(originalRequest, penaltyInfo.pTicket);

    if (penaltyInfo.requiresCaptcha) {
      // CAPTCHA required - call global handler
      if (!globalCaptchaHandler) {
        throw new Error(
          'CAPTCHA required but no global handler registered. ' +
          'Call registerGlobalCaptchaHandler() in your app initialization.'
        );
      }

      console.log('🔐 CAPTCHA required, calling global handler...');

      try {
        // Handler opens modal, user solves during countdown, returns token
        const captchaToken = await globalCaptchaHandler(
          penaltyInfo.pTicket,
          penaltyInfo.pTime
        );

        console.log('✅ CAPTCHA solved, token received');

        // Add captcha token to request
        retryRequest = this.addCaptchaToRequest(retryRequest, captchaToken);

      } catch (error) {
        console.error('❌ CAPTCHA handler failed:', error);
        throw new Error(`CAPTCHA verification failed: ${error}`);
      }

    } else {
      // No CAPTCHA - just wait for penalty period
      await this.waitForPenalty(penaltyInfo.pTime);
    }

    // Retry the request with p-ticket (and captcha if applicable)
    console.log('🔄 Retrying request with p-ticket...');
    const retryResult = await retryFn(retryRequest);
    console.log('✅ Retry successful');

    return retryResult;
  }
}
